import React, { useEffect, useState } from 'react';
import Input from '../../../../../../../components/Input';
import {
  MultiSelect as MantineMultiSelect,
  Tooltip,
  ActionIcon,
  Flex,
  Button
} from '@mantine/core';
import { FormElement, TableHeader, TableBodyFormPF } from '../../style';
import {
  TableWrapper,
  SectionOptionLabel,
  SectionsInfoIcon,
  FundNameHeader,
  FundCount
} from './style';
import Checkbox from '../../../../../../../components/Checkbox';
import FormPFBaseCurrencyField from './FormPFBaseCurrencyField';
import { IoIosArrowDown } from 'react-icons/io';
import { MdOutlineDeleteOutline } from 'react-icons/md';

const FUND_TYPES = [
  'Hedge Fund',
  'Liquidity Fund',
  'Other Private Funds',
  'Private Equity Fund',
  'Real Estate Fund',
  'Securitized Asset Fund',
  'Venture Capital Fund',
  'Funds and accounts other than private funds'
];

const DEFAULT_SECTIONS = ['1b'];

const SECTION_OPTIONS = [
  {
    value: '1b',
    label: '1b',
    disabled: true,
    required: true,
    tooltip: 'Section 1b — Basic fund information. Required for all funds and cannot be deselected.'
  },
  {
    value: '1c',
    label: '1c',
    required: false,
    tooltip: 'Section 1c — Strategy type, trading activity & counterparty exposures'
  },
  {
    value: '2b',
    label: '2b',
    required: false,
    tooltip:
      'If you are required to complete Section 2a, you must complete a separate Section 2b with respect to each qualifying hedge fund that you advise. Qualifying Hedge Fund: Any hedge fund that has a net asset value (individually or in combination with any feeder funds, parallel funds and/or dependent parallel managed accounts) of at least $500 million as of the last day of any month in the fiscal quarter immediately preceding your most recently completed fiscal quarter.'
  }
];

export const FormPFFundInformationTable = ({
  funds,
  forceExpand,
  handleIsFundIncludedChange,
  closeForceExpand,
  handleFundBaseCurrencyChange,
  handleFundTypeChange,
  handleFundSectionsChange,
  handleFundInputChange,
  handleDeleteFund,
  managerSections
}) => {
  const [showMore, setShowMore] = useState(false);

  const is2aSelected = Array.isArray(managerSections) && managerSections.includes('2a');

  const getSectionOptions = () =>
    SECTION_OPTIONS.map(opt => (opt.value === '2b' ? { ...opt, disabled: !is2aSelected } : opt));

  const renderSectionOption = ({ option }) => {
    const isDisabledBy2a = option.value === '2b' && !is2aSelected;
    const tooltipLabel = isDisabledBy2a
      ? 'Section 2a (Manager) must be selected to enable Section 2b'
      : option.tooltip;
    return (
      <Tooltip
        label={tooltipLabel}
        disabled={!tooltipLabel}
        position="right"
        withArrow
        multiline
        w={320}
      >
        <SectionOptionLabel>{option.label}</SectionOptionLabel>
      </Tooltip>
    );
  };

  const renderFunds = () => {
    let fundsToShow = funds || [];

    if (!showMore && funds?.length > 6) {
      fundsToShow = funds.slice(0, 2);
    }

    return fundsToShow.map(fund => {
      if (!fund?.id) return null;

      const fundTypes = fund.fund_types?.map(fType => {
        if (typeof fType === 'string') return fType;
        if (fType?.fund_type) return fType?.fund_type;
        return fType;
      });

      return (
        <tr key={fund.id}>
          <td className={!fund.is_included && 'not_included'}>
            <div className="label">
              <Checkbox
                onChange={() => handleIsFundIncludedChange(!fund.is_included, fund.id)}
                checked={fund.is_included}
                className="checkbox"
              />
              {fund.name}
            </div>
          </td>
          <td className={!fund.is_included && 'not_included'}>
            <MantineMultiSelect
              w="100%"
              placeholder=""
              rightSection={<IoIosArrowDown />}
              data={getSectionOptions()}
              disabled={!fund.is_included}
              value={fund.required_sections || DEFAULT_SECTIONS}
              onChange={selected => {
                // Section 1b is mandatory always keep it selected
                const withMandatory = selected.includes('1b') ? selected : ['1b', ...selected];
                handleFundSectionsChange(withMandatory, fund.id);
              }}
              renderOption={renderSectionOption}
              styles={{
                pill: { fontSize: '11px', height: '20px', padding: '0 6px', whiteSpace: 'nowrap' },
                pillsList: { rowGap: '2px', flexWrap: 'wrap' }
              }}
            />
          </td>

          <td className={!fund.is_included && 'not_included'}>
            <FormElement>
              <Input
                required={fund.is_included}
                value={fund.sec_number}
                name="sec_number"
                onChange={e => handleFundInputChange(e.target.name, e.target.value, fund.id)}
                placeholder="SEC ID"
                minLength={14}
                maxLength={14}
              />
            </FormElement>
          </td>

          <td className={!fund.is_included && 'not_included'}>
            <FormPFBaseCurrencyField
              required
              fund={fund}
              onChange={valueObject =>
                handleFundBaseCurrencyChange(valueObject?.value || null, fund.id)
              }
            />
          </td>

          <td className={!fund.is_included && 'not_included'}>
            <MantineMultiSelect
              w="100%"
              placeholder="Select"
              rightSection={<IoIosArrowDown />}
              data={FUND_TYPES}
              onChange={rawTypes => {
                const formattedTypes = rawTypes.map(type => ({
                  fund_type: type
                }));
                handleFundTypeChange(formattedTypes, fund.id);
              }}
              value={fundTypes}
              styles={{
                pill: {
                  fontSize: '11px',
                  padding: '2px 6px',
                  maxWidth: 'none',
                  height: 'auto',
                  whiteSpace: 'nowrap'
                },
                pillsList: { gap: '3px', flexWrap: 'wrap', padding: '2px 0' },
                input: { minHeight: 32 }
              }}
            />
          </td>

          <td className={!fund.is_included && 'not_included'}>
            <FormElement>
              <Input
                value={fund.nfa_id}
                name="nfa_id"
                onChange={e => handleFundInputChange(e.target.name, e.target.value, fund.id)}
                placeholder="—"
                minLength={7}
                maxLength={7}
              />
            </FormElement>
          </td>

          <td className={!fund.is_included && 'not_included'}>
            <FormElement>
              <Input
                value={fund.lei_id}
                name="lei_id"
                onChange={e => handleFundInputChange(e.target.name, e.target.value, fund.id)}
                placeholder="—"
                minLength={20}
                maxLength={20}
              />
            </FormElement>
          </td>

          <td>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => handleDeleteFund && handleDeleteFund(fund.id)}
              aria-label="Remove fund"
            >
              <MdOutlineDeleteOutline size={16} />
            </ActionIcon>
          </td>
        </tr>
      );
    });
  };

  useEffect(() => {
    if (forceExpand) {
      setShowMore(true);
      closeForceExpand();
    }
  }, [forceExpand, closeForceExpand]);

  return (
    <>
      <TableWrapper>
        <TableHeader>
          <tr>
            <th>
              <FundNameHeader>
                <span>FUND NAME</span>
                <FundCount>
                  {funds?.length} fund{funds?.length === 1 ? '' : 's'}
                </FundCount>
              </FundNameHeader>
            </th>
            <th>
              <Flex align="center" gap={4}>
                <span>SECTIONS</span>
                <Tooltip
                  label="Select which Form PF sections apply to each fund. Section 1b is always required."
                  withArrow
                  multiline
                  w={240}
                >
                  <SectionsInfoIcon role="img" aria-label="Sections information" tabIndex={0}>
                    {'ⓘ'}
                  </SectionsInfoIcon>
                </Tooltip>
              </Flex>
            </th>
            <th>SEC ID *</th>
            <th>CURRENCY</th>
            <th>TYPE *</th>
            <th>NFA ID</th>
            <th>LEI ID</th>
            <th />
          </tr>
        </TableHeader>
        <TableBodyFormPF>{renderFunds()}</TableBodyFormPF>
      </TableWrapper>

      <Flex mb="20px" mt="8" justify="space-between" align="center">
        {funds?.length > 6 ? (
          showMore ? (
            <Button
              size="sm"
              variant="white"
              type="button"
              onClick={() => setShowMore(prev => !prev)}
            >
              Hide ({funds.length - 2})
            </Button>
          ) : (
            <Button
              size="sm"
              variant="white"
              type="button"
              onClick={() => setShowMore(prev => !prev)}
            >
              See More ({funds.length - 2})
            </Button>
          )
        ) : (
          <div />
        )}
      </Flex>
    </>
  );
};
